import axios from "axios";
import type { AxiosRequestConfig } from "axios";
import { ReqMethods, ResponseStatus } from "../enums";
import env from "../config";
import { useParams } from "react-router-dom";
// import { setNotification, useNotifcation } from "context/notification";
// import { useAuth, useLogout } from "../hook/auth";

const BASE_URL = `${env.API_URL}/api/v1`;
const APPS_BASE_URL = `${env.API_URL}/v1`;
const AGENCY_APPS_BASE_URL = `${env.API_URL}/api/agency_app`;

type RequestOptions = {
  query?: string;
  payload?: any;
  token?: boolean;
  apiKey?: string | null;
  message?: string;
  error?: string;
};
function useServiceHandler() {
  return (
    fn: (
      method: string,
      path: string,
      config: AxiosRequestConfig | any
    ) => Promise<any>
  ) =>
    async (
      method: string,
      path: string,
      { query, payload, token, apiKey, message, error }: RequestOptions = {}
    ) => {
      try {
        const res = await fn(method, path, { query, payload, token, apiKey });
        console.log("API - RESPONSE", res, message, error);

        return res.data;
      } catch (err: any) {
        console.log("API- ERROR", err.response?.data || err);

        if (
          err.response &&
          err.response.status === ResponseStatus.UNAUTHORIZED
        ) {
          return { error: err.response?.data || err };
        }

        return { error: err.response ? err.response.data : err };
      }
    };
}

function useCallService() {
  const serviceHandler = useServiceHandler();

  const CallService = (
    method: string,
    path: string,
    { query, payload, token = true, apiKey = null }: RequestOptions = {}
  ) => {
    const pathname = query ? `${path}?${query}` : path;

    const config: AxiosRequestConfig = {}; // Correct type

    if (apiKey) config.headers = { apiKey };

    const details = {
      ...(payload ? { data: payload } : {}), // Use 'data' for payload in Axios
      ...config,
    };

    return axios({
      // Use axios as a function
      method,
      url: pathname,
      ...details,
    });
  };

  return serviceHandler(CallService);
}

function useAppServices() {
  const { company_id } = useParams<{ company_id: string }>();

  const { GET, POST, PUT, DELETE } = ReqMethods;
  const serviceHandler = useCallService();

  /**
   * Option for service is the object that could have the following properties
   * query, payload, token, apiKey
   */

  const APIs = {
    auth: {
      login: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/auth/login`, options),
      signup: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/auth/signup`, options),
      access_token: (options: any) =>
        serviceHandler(POST, `${APPS_BASE_URL}/auth/access_token`, options),
    },
    utils: {
      upload_image: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/utils/upload/image`, options),
    },
    agency: {
      get: (options: any) => serviceHandler(GET, `${BASE_URL}/agency`, options),
      get_ghl_company: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/agency/get_ghl_company`, options),
      update_ghl_company: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/agency/update_ghl_company`, options),
      getProfile: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/agency/profile`, options),
      profile: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/agency/profile`, options),
      get_app_setup: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/agency/get_app_setup`, options),
      getworkflows: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/agency/getworkflows`, options),
      update: (options: any) =>
        serviceHandler(PUT, `${BASE_URL}/agency`, options),
      update_with_company_id: (options: any) =>
        serviceHandler(
          PUT,
          `${AGENCY_APPS_BASE_URL}/agency/update_with_company_id`,
          options
        ),
      getProducts: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/agency/getproducts`, options),
      getSnapshots: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/agency/list-snapshots`, options),
    },
    user: {
      get: (options: any) => serviceHandler(GET, `${BASE_URL}/user`, options),
      filter: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/user/filter`, options),
      register: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/user/register`, options),
      add_team: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/user/add_team `, options),
      location_user: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/user/location_user`, options),
      subscriber: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/user/subscriber`, options),
      agency_user: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/user/agency_user`, options),
      user_info: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/user/user_info`, options),
      all_app_users: (options: any) =>
        serviceHandler(GET, `${BASE_URL}/appusers/allappusers`, options),
    },
    ghl: {
      call: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/services/ghl`, options),
      refresh_token: (options: any) =>
        serviceHandler(POST, `${BASE_URL}/services/ghl/refresh_token`, options),
    },
    bigdata: {
      get_data: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/get-big-data`, options),
      push_to_crm: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/push-to-crm`, options),
      download_records: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/download-records`, options),
      get_stats: (options: any) => serviceHandler(GET, `${BASE_URL}/bigdata/get-stats`, options),
      get_markets: (options: any) => serviceHandler(GET, `${BASE_URL}/bigdata/get-markets`, options),
      get_locations: (options: any) => serviceHandler(GET, `${BASE_URL}/bigdata/get-locations`, options),
      get_agency: (options: any) => serviceHandler(GET, `${BASE_URL}/bigdata/get-agency`, options),
      get_location: (options: any) => serviceHandler(GET, `${BASE_URL}/bigdata/get-location`, options),
      get_location_balance: (options: any) => serviceHandler(GET, `${BASE_URL}/bigdata/location-balance`, options),
      get_custom_fields: (options: any) => serviceHandler(GET, `${BASE_URL}/bigdata/get-custom-fields`, options),
      check_resync_locations: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/check-resync-locations`, options),
      add_custom_menu: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/add-custom-menu`, options),
      remove_custom_menu: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/remove-custom-menu`, options),
      update_agency_settings: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/agency-settings`, options),
      update_location_settings: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/location-settings`, options),
      update_location_tags: (options: any) => serviceHandler(POST, `${BASE_URL}/bigdata/location-tags`, options),
    },
  };

  return APIs;
}

const useUploadImage = () => {
  const AppService = useAppServices();
  return ({
    toaster,
    file,
    desiredPath,
  }: {
    toaster: any;
    file: any;
    desiredPath: string;
  }) => {
    const form = new FormData();
    form.append("image", file, file.name);
    return AppService.utils.upload_image({
      toaster,
      payload: form,
      query: `desiredPath=${desiredPath}`,
    });
  };
};

export { useAppServices, useCallService, useUploadImage };
